import React, { useState } from "react";
import { Modal, Pressable, TextInput, View, Platform } from "react-native";
import CCText from "@/Atoms/CCText";

const inputStyle = {
  borderWidth: 1,
  borderColor: "rgba(38,35,34,0.20)",
  borderRadius: 10,
  paddingHorizontal: 13,
  paddingVertical: 11,
  fontSize: 14,
  color: "#262322",
  backgroundColor: "#fff",
  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null),
};

// Admin create-poll sheet, opened from the chat "+" composer.
export default function CreatePollSheet({
  visible,
  onCancel,
  onCreate,
  submitting,
}: {
  visible: boolean;
  onCancel: () => void;
  onCreate: (question: string, options: string[]) => void;
  submitting?: boolean;
}) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);

  const reset = () => {
    setQuestion("");
    setOptions(["", ""]);
  };

  const filled = options.map((o) => o.trim()).filter(Boolean);
  const valid = question.trim().length > 0 && filled.length >= 2;

  const setOption = (i: number, v: string) =>
    setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));

  const submit = () => {
    if (!valid || submitting) return;
    onCreate(question.trim(), filled);
    reset();
  };

  const cancel = () => {
    reset();
    onCancel();
  };

  return (
    <Modal transparent animationType="fade" visible={visible} onRequestClose={cancel}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(38,35,34,0.45)",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <View
          testID="create-poll-sheet"
          style={{
            backgroundColor: "#FAF6EB",
            borderRadius: 18,
            padding: 20,
            width: "100%",
            maxWidth: 420,
          }}
        >
          <CCText fontWeight="thunder" style={{ fontSize: 22, color: "#262322", marginBottom: 14 }}>
            Create a poll
          </CCText>

          <CCText style={{ fontSize: 12, fontWeight: "600", color: "#6B584D", marginBottom: 6 }}>
            Question
          </CCText>
          <TextInput
            testID="poll-question-input"
            value={question}
            onChangeText={setQuestion}
            placeholder="e.g. Blitz or Rapid this Saturday?"
            placeholderTextColor="#A8A7A6"
            style={inputStyle as any}
          />

          <CCText style={{ fontSize: 12, fontWeight: "600", color: "#6B584D", marginTop: 14, marginBottom: 6 }}>
            Options
          </CCText>
          <View style={{ gap: 8 }}>
            {options.map((o, i) => (
              <TextInput
                key={i}
                testID={`poll-option-input-${i}`}
                value={o}
                onChangeText={(v) => setOption(i, v)}
                placeholder={`Option ${i + 1}`}
                placeholderTextColor="#A8A7A6"
                style={inputStyle as any}
              />
            ))}
          </View>
          {options.length < 4 ? (
            <Pressable
              testID="poll-add-option"
              onPress={() => setOptions((p) => [...p, ""])}
              style={{ paddingVertical: 8, marginTop: 4, alignSelf: "flex-start" }}
            >
              <CCText style={{ fontSize: 13, fontWeight: "600", color: "#3DAB9E" }}>+ Add option</CCText>
            </Pressable>
          ) : null}

          <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
            <Pressable
              testID="poll-cancel"
              onPress={cancel}
              style={{
                flex: 1,
                borderWidth: 1.5,
                borderColor: "#4D3F37",
                borderRadius: 10,
                paddingVertical: 12,
                alignItems: "center",
              }}
            >
              <CCText style={{ fontSize: 14, fontWeight: "600", color: "#4D3F37" }}>Cancel</CCText>
            </Pressable>
            <Pressable
              testID="poll-create-submit"
              disabled={!valid || submitting}
              onPress={submit}
              style={{
                flex: 1,
                backgroundColor: valid && !submitting ? "#3DAB9E" : "#B7D9D3",
                borderRadius: 10,
                paddingVertical: 12,
                alignItems: "center",
              }}
            >
              <CCText style={{ fontSize: 14, fontWeight: "600", color: "#fff" }}>
                {submitting ? "Creating…" : "Create poll"}
              </CCText>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
